import { getTransactions } from 'apis';
import { Intro, DataTable, PopOverMenu } from 'components/commons';
import { useApi, useFilter, useModal, useMount } from 'hooks';
import React, { useCallback, useMemo } from 'react';
import { formatAmount } from 'utils';
import { booksOfAccountsColumns } from './books-of-accounts-columns';
import { booksOfAccountsGeneralLedgerColumns } from './books-of-accounts-general-ledger-columns';

import { booksOfAccountsFilterState } from './books-of-accounts-filter.state';
import styles from './books-of-accounts.module.scss';
import { SuccessModal } from 'components/modals';
import { Path } from 'enums';
import { useHistory } from 'react-router-dom';
import { dateTimeFormatB } from 'utils/date.utils';
import { prettifyBooksOfAccounts } from 'utils/pretty.utils';
import BooksOfAccounts from 'enums/books-of-accounts';
import { booksOfAccountsCashReceiptColumns } from './books-of-accounts-cash-receipt-columns';
import { booksOfAccountsCashDisbursementColumns } from './books-of-accounts-cash-disbursement-columns';
import { exportToExcel } from 'utils/export.utils';
import BooksOfAccountsFilter from './books-of-accounts-filter';
import BooksOfAccountsStatusModal from './books-of-accounts-status-modal';
import BooksOfAccountsMembersModal from './books-of-accounts-members-modal';

const BooksOfAccountsModule = ({ type }) => {
  const statusModal = useModal();
  const membersModal = useModal();
  const history = useHistory();
  const successModal = useModal();

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(booksOfAccountsFilterState());

  const {
    request: getTransactionRequest,
    loading: fetchingTransactions,
    result: getTransactionsResult = { transactions: [], count: 0 }
  } = useApi({
    api: getTransactions,
    pageError: true,
    params: {
      bookType: type
    }
  });

  useMount(() => {
    fetchTransactions(requestState);
  });

  const fetchTransactions = useCallback(
    (rs) => {
      getTransactionRequest(submitFilter({ ...rs }));
    },
    [getTransactionRequest, submitFilter]
  );

  const preparedTransactionsData = useMemo(() => {
    const { transactions } = getTransactionsResult;
    if (transactions.length > 0) {
      const preparedData = transactions.map((d) => {
        const map = new Map();
        const accountNameDisplay = d.isParent || d.isTotal ? <strong>{`${d.accountName}`}</strong> : d.accountName;
        let debitDisplay = '';
        if (d.debit) {
          debitDisplay = d.isTotal ? <strong>{`${formatAmount(d.debit)}`}</strong> : formatAmount(d.debit);
        }
        let creditDisplay = '';
        if (d.credit) {
          creditDisplay = d.isTotal ? <strong>{`${formatAmount(d.credit)}`}</strong> : formatAmount(d.credit);
        }

        map.set('account', accountNameDisplay);
        map.set('accountCode', d.isParent ? '' : d.accountCode);
        map.set('category', d.accountCategoryName);
        map.set('parentAccountName', d.parentAccountName);
        map.set('debit', d.isParent ? '' : debitDisplay);
        map.set('credit', d.isParent ? '' : creditDisplay);
        map.set('amount', d.amount ? formatAmount(d.amount) : '');
        map.set('transactedAt', d.transactedAt ? dateTimeFormatB(d.transactedAt) : '');
        map.set('remarks', d?.remarks);
        map.set('reference', d?.orNumber || d?.cashVoucherNumber || d?.remarks);
        map.set('orNumber', d?.orNumber);
        map.set('cashVoucherNumber', d?.cashVoucherNumber);
        map.set('batchId', d.batchId);
        map.set('isParent', d.isParent);
        map.set('isTotal', d.isTotal);
        map.set('payee', d.payee ?? '');

        let options = [];

        if (['21110', '30132'].includes(d.accountCode)) {
          let details;

          details = {
            content: 'View Members',
            onClick: () => {
              membersModal.show({
                batchId: d.batchId,
                accountId: d.accountId
              });
            }
          };

          options.push(details);
        }

        if (['admin', 'super-admin'].includes(localStorage.getItem('role'))) {
          let editDetails;

          editDetails = {
            content: 'Delete Transaction',
            onClick: () => {
              statusModal.show({
                batchId: d.batchId
              });
            }
          };
          options.push(editDetails);
        }

        if (options.length > 0) {
          map.set('options', <PopOverMenu options={options} />);
        }

        return map;
      });
      return preparedData;
    }
    return [];
  }, [getTransactionsResult, membersModal, statusModal]);

  const onTypeChangeCb = useCallback(
    (type) => {
      const { requestState } = modifyFilters({ type, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onClearFilterCb = useCallback(() => {
    const { requestState } = modifyFilters({ startDate: null, endDate: null, accountId: null, searchKey: '', page: 1 });
    fetchTransactions(requestState);
  }, [fetchTransactions, modifyFilters]);

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onDateRangeCb = useCallback(
    (value) => {
      const { startDate, endDate } = value;
      const { requestState } = modifyFilters({ startDate, endDate, page: 1 });
      fetchTransactions(requestState);
    },
    [modifyFilters, fetchTransactions]
  );

  const onAccountChangeCb = useCallback(
    ({ value, label }) => {
      const { requestState } = modifyFilters({ accountId: { value, label }, page: 1 });
      fetchTransactions({ ...requestState, accountId: value });
    },
    [modifyFilters, fetchTransactions]
  );

  const onUserChangeCb = useCallback(
    (user) => {
      const { requestState } = modifyFilters({ user, username: user.value, page: 1 });

      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onRouteChangeCb = useCallback(
    (route) => {
      const { requestState } = modifyFilters({ route, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  let tableColumns = booksOfAccountsColumns;
  switch (type) {
    case BooksOfAccounts.GeneralLedger:
      tableColumns = booksOfAccountsGeneralLedgerColumns;
      break;
    case BooksOfAccounts.CashDisbursement:
      tableColumns = booksOfAccountsCashDisbursementColumns;
      break;
    case BooksOfAccounts.CashReceipt:
      tableColumns = booksOfAccountsCashReceiptColumns;
      break;
    default:
      tableColumns = booksOfAccountsColumns;
      break;
  }

  const onDownloadFileCb = async () => {
    const unixTimestamp = Math.floor(Date.now() / 1000);

    exportToExcel(preparedTransactionsData, tableColumns, `${type}-${unixTimestamp}.xlsx`, prettifyBooksOfAccounts(type));
  };

  return (
    <div>
      <SuccessModal {...successModal} />
      <BooksOfAccountsStatusModal refetchTransactions={() => fetchTransactions(requestState)} {...statusModal} />
      <BooksOfAccountsMembersModal refetchTransactions={() => fetchTransactions(requestState)} {...membersModal} />
      <div>
        <Intro
          title={prettifyBooksOfAccounts(type)}
          subtitle={'View Recorded Transactions for Accounting Books and Monitoring Purposes'}
          actionText={'Transaction Entry'}
          actionOnClick={() => {
            history.push(`${Path.Transaction}/entry`);
          }}
        />
      </div>
      <div className={styles.filters}>
        <div>
          <BooksOfAccountsFilter
            filterState={filterState}
            onTypeChange={onTypeChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            onDateRangeChange={onDateRangeCb}
            onAccountChange={onAccountChangeCb}
            onRouteChange={onRouteChangeCb}
            onUserChange={onUserChangeCb}
            onDownloadFile={onDownloadFileCb}
            onClearFilter={onClearFilterCb}
            bookAccountType={type}
          />
        </div>
      </div>

      <div className={styles.table}>
        <DataTable
          isBatchGroup={[
            BooksOfAccounts.GeneralJournal,
            BooksOfAccounts.CashDisbursement,
            BooksOfAccounts.CashReceipt,
            BooksOfAccounts.GeneralEntriesBook,
            BooksOfAccounts.GeneralLedger
          ].includes(type)}
          loading={fetchingTransactions}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={tableColumns}
          data={preparedTransactionsData}
          dataCount={getTransactionsResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
          hidePagination={type === BooksOfAccounts.GeneralLedger}
        />
      </div>
    </div>
  );
};

export default BooksOfAccountsModule;
